// keuangan.view — menu 💰 Keuangan terpadu (Apo 21 Jul): Ringkasan · Laba-Rugi · Arus Kas ·
// Posisi Keuangan · Beban. Menggantikan menu Laba-Rugi & Arus Kas yang terpisah.
//
// Aturan yang dijaga: belanja ASET tidak masuk Laba-Rugi (masuk Arus Kas investasi + Posisi
// Keuangan), lalu dicicil lewat penyusutan. Basis tetap kas; komisi teknisi tidak tersentuh
// karena basisnya omzet−HPP, bukan laba bersih.
import { rp, esc, rpFmt, rpVal, todayISO } from '@domain/format';
import { opsiKelompok } from '@modules/settings/akunkas.view';
import { currentUser } from '@core/session';
import { getSetting, saveSetting } from '@core/settings';
import { toast } from '@ui/toast';
import { openModal } from '@ui/modal';
import { downloadXLS } from '@ui/xls';
import { renderBeban, renderLabaRugi, renderArusKas } from './finance.view';
import { goto } from '@core/nav';
import { setStatusHubTab } from '@modules/payments/status-hub.view';
import { setPayFilter } from '@modules/payments/payments.view';
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { listCabang } from '@modules/cabang/cabang.repo';
import { listAkun, setSaldoAwal } from '@modules/payments/akunkas.repo';
import { susunNeraca, mergeCoa, isCoaBawaan, tipeFromKode, type ExtraRow } from '@domain/coa';
import { hppNormalOf } from '@domain/money';
import * as kRepo from './keuangan.repo';
import {
  penyusutanPadaBulan, nilaiBuku, ketahananKas, dso, bep, konsentrasi,
  KELOMPOK_ASET, wajibGarisLurus,
  analisaSehat, persen, DEFAULT_AMBANG, type Temuan, type AmbangSehat,
} from '@domain/finansial';

type Tab = 'ringkas' | 'labarugi' | 'aruskas' | 'posisi' | 'beban';
let kTab: Tab = 'ringkas';
let poScope = 'KONSOL'; // Posisi Keuangan: 'KONSOL' (semua) | kode cabang. Saldo awal per-entitas (+ PUSAT).
let poBln = 0, poThn = 0; // Neraca as-of: 0 = sekarang (semua waktu); else akhir bulan/tahun terpilih.
let poMulti = false, poN = 6; // Neraca multi-periode: kolom per akhir-bulan.

const TABS: [Tab, string][] = [
  ['ringkas', '📊 Ringkasan'], ['labarugi', '📈 Laba-Rugi'], ['aruskas', '💵 Arus Kas'],
  ['posisi', '🏦 Posisi Keuangan'], ['beban', '💸 Beban'],
];

/** Drill "klik indikator masalah → ke sumbernya". Tab keuangan = pindah tab; lainnya = pindah menu. */
function drillFinance(d: string): void {
  switch (d) {
    case 'piutang': setStatusHubTab('piutang'); goto('bayar'); break;
    case 'hutang': setStatusHubTab('hutang'); goto('bayar'); break;
    case 'dp': setStatusHubTab('dp'); goto('bayar'); break;
    case 'labarugi': case 'aruskas': case 'posisi': case 'beban': kTab = d; goto('keuangan'); break;
    case 'crm': goto('crm'); break;
    case 'lapservis': goto('lap_servis'); break;
  }
}
/** Pasang listener klik+keyboard pada semua elemen [data-drill] di dalam host. */
function wireDrill(host: HTMLElement): void {
  host.querySelectorAll<HTMLElement>('[data-drill]').forEach((el) => {
    const d = el.dataset['drill'] ?? '';
    el.addEventListener('click', () => drillFinance(d));
    el.addEventListener('keydown', (e) => { const ke = e as KeyboardEvent; if (ke.key === 'Enter' || ke.key === ' ') { ke.preventDefault(); drillFinance(d); } });
  });
}
/** Apakah target drill bisa dibuka peran saat ini (mis. Admin Finance tak punya menu CRM). */
function drillReachable(d: string, role: string | undefined): boolean {
  if (d === 'crm') return role === 'Owner' || role === 'Manager' || role === 'SPV' || role === 'CS';
  return true;
}

export function renderKeuangan(root: HTMLElement): void {
  const bar = `<div class="nt-tabs" style="border-radius:0;margin:10px 0 0">${TABS.map(([k, l]) =>
    `<button data-ktab="${k}" class="${kTab === k ? 'active' : ''}">${l}</button>`).join('')}</div>`;
  root.innerHTML = `<div class="ttl">💰 Keuangan</div>${bar}<div id="ku_pane"></div>`;
  root.querySelectorAll<HTMLElement>('button[data-ktab]').forEach((b) => b.addEventListener('click', () => {
    kTab = b.dataset['ktab'] as Tab; renderKeuangan(root);
  }));
  const pane = root.querySelector('#ku_pane') as HTMLElement;
  if (kTab === 'labarugi') { void renderLabaRugi(pane); return; }
  if (kTab === 'aruskas') { void renderArusKas(pane); return; }
  if (kTab === 'beban') { void renderBeban(pane); return; }
  if (kTab === 'posisi') { void renderPosisi(pane); return; }
  void renderRingkasan(pane);
}

// ── Ringkasan: indikator + analisa ────────────────────────────────────────────
const ambang = (): AmbangSehat => ({ ...DEFAULT_AMBANG, ...getSetting('sehat_ambang', {}) });

const kartu = (lbl: string, nilai: string, warna = '', ket = ''): string =>
  `<div class="stat"><div class="lbl">${esc(lbl)}</div><div class="num"${warna ? ` style="color:${warna}"` : ''}>${nilai}</div>${
    ket ? `<div style="font-size:11px;color:var(--muted);margin-top:2px">${esc(ket)}</div>` : ''}</div>`;

const WARNA: Record<Temuan['tingkat'], string> = { baik: 'var(--ok)', perhatian: 'var(--warn)', bahaya: 'var(--danger)' };
const IKON: Record<Temuan['tingkat'], string> = { baik: '✅', perhatian: '⚠️', bahaya: '🚨' };

async function renderRingkasan(host: HTMLElement): Promise<void> {
  host.innerHTML = '<div class="panel"><div class="empty">Menghitung…</div></div>';
  const { financeSummary } = await import('./finance.view');
  const hari = todayISO();
  const dari = `${hari.slice(0, 7)}-01`, sampai = hari;

  let agg: Awaited<ReturnType<typeof financeSummary>>;
  let aset: kRepo.AsetRow[] = [];
  let perPelanggan: number[] = [], ulang = 0;
  try {
    [agg, aset, perPelanggan, ulang] = await Promise.all([
      financeSummary(),
      kRepo.listAset().catch(() => []),
      kRepo.pendapatanPerPelanggan(dari, sampai).catch(() => []),
      kRepo.nilaiServisUlang(dari, sampai).catch(() => 0),
    ]);
  } catch (e) { host.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const ym = hari.slice(0, 7);
  const susut = aset.reduce((a, x) => a + penyusutanPadaBulan(x, ym), 0);
  // HPP normal (anti-dobel: kurangi part yang sudah diakui sementara) + HPP sementara — selaras Laba-Rugi.
  const hppNormalR = hppNormalOf(agg.hpp, agg.hppPriorPaid);
  const labaKotor = agg.omzet - hppNormalR - agg.hppSementara;
  const labaBersih = labaKotor + agg.biayaSimpan + agg.pemulihanTeknisi - agg.beban - susut;
  const marginKotor = persen(labaKotor, agg.omzet), marginBersih = persen(labaBersih, agg.omzet);
  const kasBersih = agg.kasMasuk + agg.biayaSimpan - agg.refund - agg.hpp - agg.beban;
  const tahan = ketahananKas(Math.max(0, kasBersih), agg.beban);
  const hariPeriode = +hari.slice(8, 10);
  const dsoHari = dso(agg.piutangBalance, agg.omzet, hariPeriode || 30);
  const titikImpas = bep(agg.beban + susut, marginKotor);
  const kons = konsentrasi(perPelanggan, 5);
  const kualitasP = persen(ulang, agg.omzet);

  const temuan = analisaSehat({
    pendapatan: agg.omzet, labaBersih, marginBersihPersen: marginBersih,
    marginBulanTerakhir: [marginBersih],          // riwayat margin menyusul (butuh agregat 12 bln)
    kasTahan: tahan, piutangMacet: agg.piutangBalance,
    konsentrasiTeratas: kons.teratas, biayaKualitasPersen: kualitasP, bepRp: titikImpas,
  }, ambang());

  const fmtBulan = (n: number): string => (n === Infinity ? 'tak terbatas' : `${n.toFixed(1)} bln`);

  host.innerHTML = `
    <div class="scope-note">Periode <b>bulan berjalan</b> · basis kas (pendapatan diakui saat pembayaran diverifikasi). Belanja aset tidak mengurangi laba — hanya penyusutannya.</div>

    <div class="stat-grid">
      ${kartu('Pendapatan', rp(agg.omzet))}
      ${kartu('Laba kotor', rp(labaKotor), 'var(--navy)', `margin ${marginKotor.toFixed(1)}%`)}
      ${kartu('Laba bersih', rp(labaBersih), labaBersih >= 0 ? 'var(--ok)' : 'var(--danger)', `margin ${marginBersih.toFixed(1)}%`)}
      ${kartu('Kas bersih', rp(kasBersih), kasBersih >= 0 ? 'var(--ok)' : 'var(--danger)')}
    </div>

    <div class="ttl" style="font-size:15px;margin:16px 0 6px">Indikator Kesehatan</div>
    <div class="stat-grid">
      ${kartu('Beban ÷ pendapatan', `${persen(agg.beban, agg.omzet).toFixed(1)}%`)}
      ${kartu('Ketahanan kas', fmtBulan(tahan), tahan < ambang().kasBulanBahaya ? 'var(--danger)' : (tahan < ambang().kasBulanPerhatian ? 'var(--warn)' : 'var(--ok)'), 'kas ÷ beban bulanan')}
      ${kartu('Umur piutang (DSO)', `${dsoHari.toFixed(0)} hari`, '', rp(agg.piutangBalance) + ' beredar')}
      ${kartu('Titik impas', titikImpas === null ? '—' : rp(titikImpas), agg.omzet >= (titikImpas ?? 0) ? 'var(--ok)' : 'var(--danger)', titikImpas === null ? 'margin kotor ≤ 0' : (agg.omzet >= titikImpas ? 'sudah terlampaui' : 'belum tercapai'))}
      ${kartu('Konsentrasi 5 pelanggan', `${kons.persen.toFixed(0)}%`, kons.teratas > ambang().konsentrasiPersen ? 'var(--warn)' : '', `terbesar ${kons.teratas.toFixed(0)}%`)}
      ${kartu('Biaya servis ulang', `${kualitasP.toFixed(1)}%`, kualitasP > ambang().kualitasPersen ? 'var(--warn)' : '', rp(ulang))}
    </div>

    <div class="ttl" style="font-size:15px;margin:16px 0 6px">Analisa Otomatis</div>
    <div class="panel" style="padding:10px">
      ${temuan.map((t) => { const bisa = !!t.drill && drillReachable(t.drill, currentUser()?.role);
        return `<div class="hist"${bisa ? ` data-drill="${esc(t.drill!)}" role="button" tabindex="0" title="Klik untuk membuka sumbernya"` : ''} style="border-left:3px solid ${WARNA[t.tingkat]}${bisa ? ';cursor:pointer' : ''}">
          <div class="h1"><span>${IKON[t.tingkat]} <b>${esc(t.judul)}</b></span>${bisa ? '<span class="tno-link" style="font-size:11.5px">buka sumbernya ↗</span>' : ''}</div>
          <div class="h2">${esc(t.pesan)}</div></div>`; }).join('')}
      <div style="font-size:11px;color:var(--muted);margin-top:8px">Dihitung dari angka Anda sendiri memakai ambang yang bisa disetel — bukan tebakan. Ambang diatur di Pengaturan → ⏱️ SLA & Waktu.</div>
    </div>`;
  wireDrill(host);
}

// ── Posisi Keuangan + perubahan ekuitas ───────────────────────────────────────
async function renderPosisi(host: HTMLElement): Promise<void> {
  if (poMulti) return renderNeracaMulti(host);
  host.innerHTML = '<div class="panel"><div class="empty">Menghitung…</div></div>';
  const { financeSummary } = await import('./finance.view');
  const u = currentUser();
  const bolehUbah = ['Owner', 'Manager'].includes(u?.role ?? '');
  const awalMap = kRepo.normalizeSaldoAwal(getSetting('saldo_awal', {}));
  let cabList: string[] = [];
  try { cabList = (await listCabang()).map((o) => o.kode); } catch { /* fallback: hanya Konsolidasi */ }
  if (poScope !== 'KONSOL' && !cabList.includes(poScope)) poScope = 'KONSOL'; // clamp stale
  const konsol = poScope === 'KONSOL';
  const scopeArg = konsol ? undefined : poScope;
  // As-of Neraca: 0 = sekarang; else akhir bulan terpilih (kumulatif s/d tanggal itu).
  const asOf = (poBln && poThn) ? `${poThn}-${String(poBln).padStart(2, '0')}-${new Date(poThn, poBln, 0).getDate()}` : null;
  // COA custom (buatan pengguna) + saldo manual per-entitas — dari settings (bisa Apo tambah sendiri).
  const customCoa = getSetting<{ kode: string; nama: string }[]>('coa_custom', []);
  const coaSaldo = getSetting<Record<string, Record<string, number>>>('coa_saldo', {});
  const coaMap = new Map(mergeCoa(customCoa).map((c) => [c.kode, c.nama]));

  let agg: Awaited<ReturnType<typeof financeSummary>>;
  let aset: kRepo.AsetRow[] = [], eq: kRepo.EkuitasRow[] = [], saldoAkun: kRepo.SaldoAkun[] = [];
  let payables: { outlet: string | null; serah: unknown; payments: unknown }[] = [];
  try {
    [agg, aset, eq, saldoAkun] = await Promise.all([
      financeSummary(true, scopeArg, asOf),       // scopeArg = per-cabang; asOf = kumulatif s/d akhir bulan
      kRepo.listAset().catch(() => []),
      kRepo.listEkuitas().catch(() => []),
      kRepo.saldoAkunSemua().catch(() => [] as kRepo.SaldoAkun[]),
    ]);
    try { const { listPayables } = await import('@modules/sparepart/sparepart.repo'); payables = await listPayables() as typeof payables; } catch { /* hutang supplier opsional */ }
  } catch (e) { host.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }
  const { serahTotal, paidTotal, outstanding } = await import('@domain/sparepart');

  // Scope: Konsolidasi = semua; per-cabang = hanya entitas itu (outlet cocok). Item company-wide
  // (outlet null: mis. beban HQ, aset pusat) hanya muncul di Konsolidasi — konsisten dg disclaimer LR.
  const inScope = (o: string | null): boolean => konsol || (o ?? null) === poScope;
  aset = aset.filter((x) => inScope(x.outlet));
  eq = eq.filter((x) => inScope(x.outlet));
  const rinciAkun = saldoAkun.filter((x) => inScope(x.outlet)).filter((x) => x.saldo !== 0 || x.jenis === 'tunai');
  // Hutang supplier per-scope (financeSummary.hutangSupplier selalu global → hitung ulang terfilter).
  const hutangSupplier = payables.filter((r) => inScope(r.outlet ?? null))
    .reduce((a, r) => a + outstanding(serahTotal(r.serah as never), paidTotal(r.payments as never)), 0);

  // Saldo awal untuk scope: per-cabang pakai entitasnya; Konsolidasi = jumlah semua entitas (+PUSAT).
  const awal: kRepo.SaldoAwal = konsol
    ? Object.values(awalMap).reduce((a, s) => ({ kasAwal: a.kasAwal + s.kasAwal, modalAwal: a.modalAwal + s.modalAwal, tglAwal: [a.tglAwal, s.tglAwal].filter(Boolean).sort()[0] ?? '' }), { ...kRepo.DEFAULT_SALDO_AWAL })
    : (awalMap[poScope] ?? { ...kRepo.DEFAULT_SALDO_AWAL });

  const hari = todayISO();
  const grossAset = aset.reduce((a, x) => a + (x.dijualTgl ? 0 : x.harga), 0);
  const susutTotal = aset.reduce((a, x) => a + (x.harga - nilaiBuku(x, hari)), 0);
  const setoran = eq.filter((x) => x.jenis === 'setoran').reduce((a, x) => a + x.jumlah, 0);
  const prive = eq.filter((x) => x.jenis === 'prive').reduce((a, x) => a + x.jumlah, 0);

  // Kas basis-kas (audit M1/M2): kurangi hanya yang BENAR-BENAR keluar kas (HPP/beban terbayar).
  // HPP Sementara = part yang sudah DIBAYAR ke supplier utk unit belum diambil → kas benar-benar keluar
  // (tak tertangkap hppPaid karena agg.hpp cuma unit yang sudah diambil). Kurangi kas & laba sama besar
  // → Neraca tetap seimbang, dan otomatis pindah ke HPP normal saat unit diambil.
  const bebanPaid = Math.max(0, agg.beban - agg.bebanBelumBayar);
  const hppPaid = Math.max(0, agg.hpp - hutangSupplier);
  const kas = awal.kasAwal + agg.kasMasuk + agg.biayaSimpan - agg.refund - hppPaid - agg.hppSementara - bebanPaid - grossAset + setoran - prive;
  const terpetakan = rinciAkun.reduce((a, x) => a + x.saldo, 0);
  const penampung = Math.round(kas - terpetakan); // "Belum ditempel ke akun" — idealnya 0
  const labaAkum = agg.omzet - agg.hpp - agg.hppSementara + agg.biayaSimpan + agg.pemulihanTeknisi - agg.beban - susutTotal;

  // Akun COA custom bersaldo manual (kode non-bawaan, tipe aset/liabilitas/ekuitas) → baris tambahan.
  // Konsolidasi = jumlah semua entitas; per-cabang = entitas itu saja.
  const saldoManual = (kode: string): number => konsol
    ? Object.values(coaSaldo).reduce((a, m) => a + (m[kode] ?? 0), 0)
    : (coaSaldo[poScope]?.[kode] ?? 0);
  const extra: ExtraRow[] = [...new Set(Object.values(coaSaldo).flatMap((m) => Object.keys(m)))]
    .filter((k) => !isCoaBawaan(k) && ['aset', 'liabilitas', 'ekuitas'].includes(tipeFromKode(k)))
    .map((k) => ({ kode: k, nama: coaMap.get(k) ?? k, nilai: saldoManual(k) }))
    .filter((e) => e.nilai !== 0);

  // ── Neraca berformat COA (SAK EMKM) + cek keseimbangan ──
  const n = susunNeraca({
    kasBank: kas, piutang: agg.piutangBalance, asetTetap: grossAset, akmPenyusutan: susutTotal,
    hutangSupplier, hutangBeban: agg.bebanBelumBayar, dpPelanggan: agg.dpTertahan,
    modal: awal.modalAwal + setoran, prive, labaDitahan: 0, labaBerjalan: labaAkum,
  }, extra);

  const kode = (k: string): string => `<span style="color:var(--muted);font-variant-numeric:tabular-nums">${k}</span> `;
  const drillOf: Record<string, string> = { '1-2': agg.piutangBalance ? 'piutang' : '', '2-1': hutangSupplier ? 'hutang' : '', '2-2': agg.bebanBelumBayar ? 'beban' : '', '2-3': agg.dpTertahan ? 'dp' : '' };
  const rowCoa = (b: { kode: string; nama: string; nilai: number }): string => {
    const d = drillOf[b.kode] ?? '';
    const dp = Math.max(0, b.kode.split('-').length - 2) * 14; // indentasi akun berjenjang
    return `<div class="fin-row"${dp ? ` style="padding-left:${dp}px"` : ''}><span${d ? ` class="tno-link" data-drill="${esc(d)}" role="button" tabindex="0" title="Buka sumbernya"` : ''}>${kode(b.kode)}${esc(b.nama)}${d ? ' ↗' : ''}</span><span>${rp(b.nilai)}</span></div>`;
  };
  const totalRow = (l: string, v: number, c = 'var(--navy)'): string => `<div class="fin-row fin-total"><span>${esc(l)}</span><span style="color:${c}">${rp(v)}</span></div>`;
  const baris = (k: string, v: number, tebal = false, warna = ''): string =>
    `<div class="fin-row${tebal ? ' fin-total' : ''}"><span>${esc(k)}</span><span${warna ? ` style="color:${warna}"` : ''}>${rp(v)}</span></div>`;

  const belumLengkap = !awal.tglAwal;
  const scopeOpt = `<option value="KONSOL" ${konsol ? 'selected' : ''}>🏢 Konsolidasi (semua cabang + pusat)</option>`
    + cabList.map((c) => `<option value="${esc(c)}" ${poScope === c ? 'selected' : ''}>${esc(c)}</option>`).join('');

  host.innerHTML = `
    <div class="toolbar" style="display:flex;gap:8px;margin:8px 0 10px;flex-wrap:wrap;align-items:center">
      <select id="po_scope">${scopeOpt}</select>
      <button class="btn" id="po_multi">📊 Multi Periode</button>
      <select id="po_bln"><option value="0" ${!poBln ? 'selected' : ''}>Sekarang</option>${['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'].map((m, i) => `<option value="${i + 1}" ${poBln === i + 1 ? 'selected' : ''}>s/d ${m}</option>`).join('')}</select>
      <select id="po_thn">${(() => { const cy = new Date().getFullYear(); let o = ''; for (let y = 2024; y <= cy + 1; y++) o += `<option value="${y}" ${(poThn || cy) === y ? 'selected' : ''}>${y}</option>`; return o; })()}</select>
      ${bolehUbah ? '<button class="btn" id="po_awal">⚙️ Saldo Awal</button><button class="btn" id="po_coa">🧾 Kelola COA</button><button class="btn" id="po_aset">🏗️ Aset</button><button class="btn" id="po_eq">💼 Modal & Prive</button>' : ''}
    </div>
    <div class="scope-note">Neraca (SAK EMKM) · ${konsol ? 'Konsolidasi' : 'cabang ' + esc(poScope)} · ${asOf ? 's/d ' + esc(asOf) : 'per ' + esc(hari)} · basis kas.${belumLengkap && bolehUbah ? ' <b>Saldo awal belum diisi</b> — isi via ⚙️ Saldo Awal.' : ''}</div>

    <div class="row2">
      <div class="panel" style="padding:12px">
        <div class="fin-judul">ASET</div>
        <div class="fin-row" id="po_kasbank_hd" role="button" tabindex="0" style="cursor:pointer" title="Klik: rincian rekening/bank/tunai (${konsol ? 'semua cabang + pusat' : 'cabang ' + esc(poScope)})">
          <span><span id="po_kasbank_chev" style="display:inline-block;width:14px;color:var(--muted)">▸</span>${kode(n.aset[0]!.kode)}${esc(n.aset[0]!.nama)} <span style="color:var(--muted);font-size:11px">(${rinciAkun.length} akun)</span></span><span>${rp(n.aset[0]!.nilai)}</span>
        </div>
        <div id="po_kasbank_sub" style="margin:2px 0 6px 22px;font-size:12px;color:var(--muted);display:none">
          ${rinciAkun.map((x) => `<div class="fin-row" style="padding:2px 0"><span>${x.kode ? `<span style="color:var(--muted);font-variant-numeric:tabular-nums">${esc(x.kode)}</span> ` : ''}${esc(x.nama)}${x.outlet ? ` · ${esc(x.outlet)}` : ''}</span><span>${rp(x.saldo)}</span></div>`).join('') || '<div class="fin-row" style="padding:2px 0"><span>Belum ada akun kas pada lingkup ini.</span><span></span></div>'}
          ${penampung !== 0 ? `<div class="fin-row" style="padding:2px 0;color:var(--warn)"><span class="tno-link" id="po_belumtempel" role="button" tabindex="0" style="color:var(--warn);border-bottom-color:var(--warn)">Belum ditempel ke akun ↗</span><span>${rp(penampung)}</span></div>` : ''}
        </div>
        ${n.aset.slice(1).map(rowCoa).join('')}
        ${totalRow('TOTAL ASET', n.totalAset)}
      </div>
      <div class="panel" style="padding:12px">
        <div class="fin-judul">LIABILITAS</div>
        ${n.liabilitas.map(rowCoa).join('')}
        ${totalRow('Total liabilitas', n.totalLiabilitas, 'var(--danger)')}
        <div class="fin-judul" style="margin-top:12px">EKUITAS</div>
        ${n.ekuitas.map(rowCoa).join('')}
        ${totalRow('Total ekuitas', n.totalEkuitas, 'var(--ok)')}
        ${totalRow('TOTAL LIABILITAS + EKUITAS', n.totalKanan)}
      </div>
    </div>
    <div class="scope-note" style="margin-top:8px;display:flex;gap:10px;align-items:center;flex-wrap:wrap;background:${n.seimbang ? 'var(--okbg)' : 'var(--warnbg)'};color:${n.seimbang ? 'var(--ok)' : 'var(--warn)'}">
      ${n.seimbang ? '✓ <b>Seimbang</b> — Aset = Liabilitas + Ekuitas.' : `⚠️ <b>Tidak seimbang · selisih ${rp(n.selisih)}</b> <button class="btn" id="po_selisih" style="border-color:var(--warn);color:var(--warn);margin-left:auto">🔍 Cari selisih</button>`}
    </div>

    <div class="ttl" style="font-size:15px;margin:16px 0 6px">Perubahan Ekuitas${konsol ? '' : ' — ' + esc(poScope)}</div>
    <div class="panel" style="padding:12px;max-width:520px">
      ${baris('Modal awal', awal.modalAwal)}
      ${baris('Setoran modal', setoran, false, 'var(--ok)')}
      ${baris('Pengambilan pemilik (prive)', -prive, false, 'var(--danger)')}
      ${baris('Laba ditahan (akumulasi)', labaAkum, false, labaAkum >= 0 ? 'var(--ok)' : 'var(--danger)')}
      ${baris('Ekuitas akhir', awal.modalAwal + setoran - prive + labaAkum, true, 'var(--navy)')}
      ${susutTotal ? `<div style="font-size:11px;color:var(--muted);margin-top:6px">Termasuk akumulasi penyusutan aset ${rp(susutTotal)} — beban non-kas.</div>` : ''}
    </div>`;

  const ulangi = (): void => { void renderPosisi(host); };
  const sc = host.querySelector('#po_scope') as HTMLSelectElement | null;
  if (sc) sc.onchange = (): void => { poScope = sc.value; ulangi(); };
  host.querySelector('#po_multi')?.addEventListener('click', () => { poMulti = true; ulangi(); });
  const blnSel = host.querySelector('#po_bln') as HTMLSelectElement | null;
  const thnSel = host.querySelector('#po_thn') as HTMLSelectElement | null;
  if (blnSel) blnSel.onchange = (): void => { poBln = +blnSel.value; poThn = poBln ? +(thnSel?.value || new Date().getFullYear()) : 0; ulangi(); };
  if (thnSel) thnSel.onchange = (): void => { if (poBln) { poThn = +thnSel.value; ulangi(); } };
  host.querySelector('#po_awal')?.addEventListener('click', () => dialogSaldoAwal(awalMap, konsol ? 'PUSAT' : poScope, cabList, ulangi));
  host.querySelector('#po_coa')?.addEventListener('click', () => dialogCoa(customCoa, coaSaldo, konsol ? 'PUSAT' : poScope, cabList, ulangi));
  host.querySelector('#po_aset')?.addEventListener('click', () => void dialogAset(ulangi));
  host.querySelector('#po_eq')?.addEventListener('click', () => void dialogEkuitas(cabList, ulangi));

  // Kas & Bank → dropdown rincian akun (rekening/bank/tunai/kas kecil), ter-scope poScope.
  const kbHd = host.querySelector('#po_kasbank_hd') as HTMLElement | null;
  const kbToggle = (): void => {
    const sub = host.querySelector('#po_kasbank_sub') as HTMLElement | null;
    const chev = host.querySelector('#po_kasbank_chev') as HTMLElement | null;
    if (!sub) return; const buka = sub.style.display === 'none';
    sub.style.display = buka ? '' : 'none'; if (chev) chev.textContent = buka ? '▾' : '▸';
  };
  kbHd?.addEventListener('click', kbToggle);
  kbHd?.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); kbToggle(); } });

  wireDrill(host); // klik baris masalah → langsung ke sumbernya (router drillFinance)
  host.querySelector('#po_belumtempel')?.addEventListener('click', () => void drillBelumTempel(penampung));
  host.querySelector('#po_selisih')?.addEventListener('click', () => analisaNeracaSelisih({ selisih: n.selisih, penampung, belumLengkap, extra, konsol }));
}

/** Neraca MULTI PERIODE — kolom per akhir-bulan (as-of), format Accurate. */
async function renderNeracaMulti(host: HTMLElement): Promise<void> {
  host.innerHTML = '<div class="panel"><div class="empty">Menghitung…</div></div>';
  const { financeSummary } = await import('./finance.view');
  let cabList: string[] = [];
  try { cabList = (await listCabang()).map((o) => o.kode); } catch { /* konsol saja */ }
  if (poScope !== 'KONSOL' && !cabList.includes(poScope)) poScope = 'KONSOL';
  const konsol = poScope === 'KONSOL'; const scopeArg = konsol ? undefined : poScope;
  const awalMap = kRepo.normalizeSaldoAwal(getSetting('saldo_awal', {}));
  const customCoa = getSetting<{ kode: string; nama: string }[]>('coa_custom', []);
  const coaSaldo = getSetting<Record<string, Record<string, number>>>('coa_saldo', {});
  const coaMap = new Map(mergeCoa(customCoa).map((c) => [c.kode, c.nama]));
  let aset: kRepo.AsetRow[] = [], eq: kRepo.EkuitasRow[] = [], payables: { outlet: string | null; serah: unknown; payments: unknown }[] = [];
  try {
    [aset, eq] = await Promise.all([kRepo.listAset().catch(() => []), kRepo.listEkuitas().catch(() => [])]);
    try { const { listPayables } = await import('@modules/sparepart/sparepart.repo'); payables = await listPayables() as typeof payables; } catch { /* opsional */ }
  } catch (e) { host.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }
  const { serahTotal, paidTotal, outstanding } = await import('@domain/sparepart');
  const inScope = (o: string | null): boolean => konsol || (o ?? null) === poScope;

  const now = new Date();
  const cols: { asOf: string; label: string }[] = [];
  for (let i = poN - 1; i >= 0; i--) { const d = new Date(now.getFullYear(), now.getMonth() - i + 1, 0); cols.push({ asOf: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getDate()}`, label: `${['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'][d.getMonth()]} ${String(d.getFullYear()).slice(2)}` }); }

  const awal: kRepo.SaldoAwal = konsol
    ? Object.values(awalMap).reduce((a, s) => ({ kasAwal: a.kasAwal + s.kasAwal, modalAwal: a.modalAwal + s.modalAwal, tglAwal: '' }), { ...kRepo.DEFAULT_SALDO_AWAL })
    : (awalMap[poScope] ?? { ...kRepo.DEFAULT_SALDO_AWAL });
  const hutangSupplierScope = payables.filter((r) => inScope(r.outlet ?? null)).reduce((a, r) => a + outstanding(serahTotal(r.serah as never), paidTotal(r.payments as never)), 0);
  const extra: ExtraRow[] = [...new Set(Object.values(coaSaldo).flatMap((m) => Object.keys(m)))]
    .filter((k) => !isCoaBawaan(k) && ['aset', 'liabilitas', 'ekuitas'].includes(tipeFromKode(k)))
    .map((k) => ({ kode: k, nama: coaMap.get(k) ?? k, nilai: konsol ? Object.values(coaSaldo).reduce((a, m) => a + (m[k] ?? 0), 0) : (coaSaldo[poScope]?.[k] ?? 0) }))
    .filter((e) => e.nilai !== 0);

  let hasil: ReturnType<typeof susunNeraca>[];
  try {
    hasil = await Promise.all(cols.map(async (c) => {
      const fs = await financeSummary(true, scopeArg, c.asOf);
      const asetTo = aset.filter((x) => inScope(x.outlet) && (!x.tglBeli || x.tglBeli <= c.asOf));
      const grossAset = asetTo.reduce((a, x) => a + (x.dijualTgl && x.dijualTgl <= c.asOf ? 0 : x.harga), 0);
      const susut = asetTo.reduce((a, x) => a + (x.harga - nilaiBuku(x, c.asOf)), 0);
      const setoran = eq.filter((x) => inScope(x.outlet) && x.jenis === 'setoran' && x.tgl <= c.asOf).reduce((a, x) => a + x.jumlah, 0);
      const prive = eq.filter((x) => inScope(x.outlet) && x.jenis === 'prive' && x.tgl <= c.asOf).reduce((a, x) => a + x.jumlah, 0);
      const kas = awal.kasAwal + fs.kasMasuk + fs.biayaSimpan - fs.refund - Math.max(0, fs.hpp - hutangSupplierScope) - fs.hppSementara - Math.max(0, fs.beban - fs.bebanBelumBayar) - grossAset + setoran - prive;
      const laba = fs.omzet - fs.hpp - fs.hppSementara + fs.biayaSimpan + fs.pemulihanTeknisi - fs.beban - susut;
      return susunNeraca({ kasBank: kas, piutang: fs.piutangBalance, asetTetap: grossAset, akmPenyusutan: susut, hutangSupplier: hutangSupplierScope, hutangBeban: fs.bebanBelumBayar, dpPelanggan: fs.dpTertahan, modal: awal.modalAwal + setoran, prive, labaDitahan: 0, labaBerjalan: laba }, extra);
    }));
  } catch (e) { host.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const fnd = (arr: { kode: string; nilai: number }[], kode: string): number => arr.find((b) => b.kode === kode)?.nilai ?? 0;
  const th = `<th style="text-align:left">Deskripsi</th>${cols.map((c) => `<th class="r">${esc(c.label)}</th>`).join('')}`;
  const row = (label: string, get: (n: typeof hasil[number]) => number, opt: { bold?: boolean; indent?: boolean; warn?: boolean } = {}): string =>
    `<tr${opt.bold ? ' style="font-weight:700;background:var(--ice)"' : ''}><td style="${opt.indent ? 'padding-left:16px;' : ''}">${esc(label)}</td>${hasil.map((n) => `<td class="r"${opt.warn ? ' style="color:var(--warn)"' : ''}>${rp(get(n))}</td>`).join('')}</tr>`;
  const head = (t: string): string => `<tr style="font-weight:700"><td colspan="${cols.length + 1}">${t}</td></tr>`;
  const rows = [
    head('ASET'),
    row('Kas & Bank', (n) => fnd(n.aset, '1-1'), { indent: true }),
    row('Piutang usaha', (n) => fnd(n.aset, '1-2'), { indent: true }),
    row('Aset tetap (neto)', (n) => fnd(n.aset, '1-5') + fnd(n.aset, '1-6'), { indent: true }),
    row('TOTAL ASET', (n) => n.totalAset, { bold: true }),
    head('LIABILITAS'),
    row('Hutang supplier', (n) => fnd(n.liabilitas, '2-1'), { indent: true }),
    row('Hutang beban', (n) => fnd(n.liabilitas, '2-2'), { indent: true }),
    row('DP pelanggan', (n) => fnd(n.liabilitas, '2-3'), { indent: true }),
    row('Total Liabilitas', (n) => n.totalLiabilitas, { bold: true }),
    head('EKUITAS'),
    row('Modal + setoran', (n) => fnd(n.ekuitas, '3-1'), { indent: true }),
    row('Prive', (n) => fnd(n.ekuitas, '3-2'), { indent: true }),
    row('Laba berjalan', (n) => fnd(n.ekuitas, '3-4'), { indent: true }),
    row('Total Ekuitas', (n) => n.totalEkuitas, { bold: true }),
    row('TOTAL LIABILITAS + EKUITAS', (n) => n.totalKanan, { bold: true }),
    row('Selisih (ideal 0)', (n) => n.selisih, { warn: true }),
  ].join('');

  const scopeOpt = `<option value="KONSOL" ${konsol ? 'selected' : ''}>🏢 Konsolidasi</option>${cabList.map((c) => `<option value="${esc(c)}" ${poScope === c ? 'selected' : ''}>${esc(c)}</option>`).join('')}`;
  host.innerHTML = `<div class="scope-note">Neraca Multi Periode (SAK EMKM) · kolom per akhir bulan (${poN} bln) · ${konsol ? 'Konsolidasi' : 'cabang ' + esc(poScope)} · basis kas.</div>
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
      <button class="btn" id="po_multi">◀ 1 Periode</button>
      <select id="po_scope">${scopeOpt}</select>
      <select id="po_n"><option value="3" ${poN === 3 ? 'selected' : ''}>3 bln</option><option value="6" ${poN === 6 ? 'selected' : ''}>6 bln</option><option value="12" ${poN === 12 ? 'selected' : ''}>12 bln</option></select>
      <button class="btn" id="po_xls" style="margin-left:auto;padding:5px 10px">⬇️ Excel</button></div>
    <div style="overflow-x:auto"><table class="data nosort"><thead><tr>${th}</tr></thead><tbody>${rows}</tbody></table></div>`;
  const ulangi = (): void => { void renderNeracaMulti(host); };
  host.querySelector('#po_multi')?.addEventListener('click', () => { poMulti = false; void renderPosisi(host); });
  const sc = host.querySelector('#po_scope') as HTMLSelectElement | null; if (sc) sc.onchange = (): void => { poScope = sc.value; ulangi(); };
  const nn = host.querySelector('#po_n') as HTMLSelectElement | null; if (nn) nn.onchange = (): void => { poN = +nn.value; ulangi(); };
  host.querySelector('#po_xls')?.addEventListener('click', () => {
    const hh = `<tr><th>Deskripsi</th>${cols.map((c) => `<th>${esc(c.label)}</th>`).join('')}</tr>`;
    const specs: [string, (n: typeof hasil[number]) => number][] = [['Kas & Bank', (n) => fnd(n.aset, '1-1')], ['Piutang usaha', (n) => fnd(n.aset, '1-2')], ['Aset tetap (neto)', (n) => fnd(n.aset, '1-5') + fnd(n.aset, '1-6')], ['TOTAL ASET', (n) => n.totalAset], ['Hutang supplier', (n) => fnd(n.liabilitas, '2-1')], ['Hutang beban', (n) => fnd(n.liabilitas, '2-2')], ['DP pelanggan', (n) => fnd(n.liabilitas, '2-3')], ['Total Liabilitas', (n) => n.totalLiabilitas], ['Modal + setoran', (n) => fnd(n.ekuitas, '3-1')], ['Prive', (n) => fnd(n.ekuitas, '3-2')], ['Laba berjalan', (n) => fnd(n.ekuitas, '3-4')], ['Total Ekuitas', (n) => n.totalEkuitas], ['TOTAL LIAB+EKUITAS', (n) => n.totalKanan], ['Selisih', (n) => n.selisih]];
    const body = specs.map(([l, g]) => `<tr><td>${esc(l)}</td>${hasil.map((n) => `<td class="r">${g(n)}</td>`).join('')}</tr>`).join('');
    downloadXLS(`neraca-multiperiode-${poN}bln.xls`, `<h2>Neraca (Multi Periode) — iFiCs</h2><p>${poN} bln · ${esc(konsol ? 'Konsolidasi' : poScope)}</p><table>${hh}${body}</table>`);
  });
}

/** Analisa "cari selisih" Neraca — pecah ketidakseimbangan jadi penyebab yang bisa ditindak. */
function analisaNeracaSelisih(p: { selisih: number; penampung: number; belumLengkap: boolean; extra: ExtraRow[]; konsol: boolean }): void {
  const rows: { judul: string; nilai: number | null; saran: string }[] = [];
  if (p.belumLengkap) rows.push({ judul: 'Saldo awal belum diisi', nilai: null, saran: 'Penyebab paling umum. Isi Kas awal & Modal awal tiap entitas via ⚙️ Saldo Awal — Neraca hanya seimbang bila modal awal = kas awal + aset awal − hutang awal.' });
  if (p.penampung !== 0) rows.push({ judul: 'Belum ditempel ke akun', nilai: p.penampung, saran: 'Kas hitungan ≠ jumlah saldo akun — nota belum diverifikasi Finance atau tanpa cara bayar. Klik "Belum ditempel ke akun" di baris Kas untuk rincian.' });
  // Akun COA manual: bila hanya dicatat sesisi (mis. nambah hutang tanpa nambah kas/aset) → tak seimbang.
  const eA = p.extra.filter((e) => tipeFromKode(e.kode) === 'aset').reduce((a, e) => a + e.nilai, 0);
  const eLE = p.extra.filter((e) => tipeFromKode(e.kode) !== 'aset').reduce((a, e) => a + e.nilai, 0);
  if (p.extra.length && eA - eLE !== 0) rows.push({ judul: 'Akun COA manual belum berpasangan', nilai: eA - eLE, saran: 'Akun manual dicatat di satu sisi saja. Setiap hutang/ekuitas baru harus diimbangi aset/kas (dan sebaliknya) agar Neraca tetap seimbang.' });
  const dikenal = (p.penampung || 0) + (p.extra.length ? eA - eLE : 0);
  const sisa = Math.round(p.selisih - dikenal);
  if (sisa !== 0) rows.push({ judul: 'Selisih pembukuan lain', nilai: sisa, saran: 'Sisa setelah penyebab di atas — biasanya modal awal belum pas, aset/penyusutan belum tercatat, atau prive/setoran terlewat. Cocokkan modal awal dengan kekayaan bersih saat mulai.' });

  const li = rows.map((r) => `<li style="margin-bottom:5px"><b>${esc(r.judul)}</b>${r.nilai != null ? ` — ${rp(r.nilai)}` : ''}<div style="font-size:12px;color:var(--muted)">${esc(r.saran)}</div></li>`).join('') || '<li>Tak terdeteksi penyebab spesifik.</li>';
  const bg = openModal(`<div class="modal-head"><h3>🔍 Cari Selisih Neraca</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note" style="background:var(--warnbg);color:var(--warn)">Selisih <b>${rp(p.selisih)}</b> = Total Aset − (Liabilitas + Ekuitas). Idealnya Rp 0.</div>
      <div style="font-weight:700;font-size:13px;margin:8px 0 4px">Kemungkinan penyebab</div>
      <ul style="margin:0;padding-left:18px;font-size:13px;line-height:1.5">${li}</ul>
    </div>
    <div class="modal-foot"><button class="btn" id="ns_close">Tutup</button></div>`);
  bg.querySelector('#ns_close')?.addEventListener('click', () => bg.remove());
}

/**
 * Rincian "Belum ditempel ke akun": pecah selisih jadi penyebab yang bisa ditindak (nota lunas
 * belum diverifikasi Finance, nota terverifikasi tanpa cara bayar) + sisa (mis. HPP/modal tanpa
 * pembayaran supplier tercatat, beban tanpa akun, saldo awal). Menggantikan drill lama yang selalu
 * ke filter "lunas belum verifikasi" — sering kosong sehingga "diklik tidak nemu".
 */
async function drillBelumTempel(penampung: number): Promise<void> {
  let unverif = 0, cU = 0, noCara = 0, cN = 0;
  try {
    const data = await fetchAllPaged<Record<string, number | boolean | null>>((a, b) => supabase().from('tickets')
      .select('biaya,dp,biaya_simpan,lunas,verified,cara_bayar,refund').eq('arsip_keu', false).order('no').range(a, b));
    for (const t of data) {
      if (t['refund']) continue;
      const bs = (Number(t['biaya']) || 0) + (Number(t['biaya_simpan']) || 0);
      if (t['lunas'] && !t['verified']) { unverif += Math.max(0, bs - (Number(t['dp']) || 0)); cU++; }
      if (t['verified'] && !t['cara_bayar']) { noCara += Math.max(0, bs); cN++; }
    }
  } catch { /* gagal ambil rincian → tetap tampilkan penjelasan umum */ }
  // penampung = (nota tanpa cara bayar) − (nota belum verifikasi) − (arus kas keluar tak tercatat di akun)
  const lain = Math.round(noCara - unverif - penampung);
  const row = (k: string, v: number, n: number, act = ''): string =>
    `<div class="fin-row" style="padding:3px 0"><span>${esc(k)}${n ? ` <span style="color:var(--muted)">(${n} nota)</span>` : ''}${act}</span><span>${rp(v)}</span></div>`;
  const bg = openModal(`<div class="modal-head"><h3>🔎 Rincian "Belum ditempel ke akun"</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Selisih antara <b>Kas (hitungan)</b> dan <b>jumlah saldo akun kas</b>. Idealnya Rp 0.</div>
      <div class="fin-row fin-total" style="margin-bottom:6px"><span>Total belum ditempel</span><span>${rp(penampung)}</span></div>
      ${row('Nota lunas belum diverifikasi Finance', unverif, cU, cU ? ' <span class="tno-link" id="bt_verif" role="button" tabindex="0" style="color:var(--blue)">buka →</span>' : '')}
      ${row('Nota terverifikasi tanpa cara bayar', noCara, cN)}
      ${row('Selisih lain (HPP/modal tanpa pembayaran supplier tercatat, beban tanpa akun, saldo awal)', lain, 0)}
      <div style="font-size:11.5px;line-height:1.5;color:var(--muted);margin-top:8px">Cara merapikan: verifikasi nota di <b>Status Pembayaran</b>, isi cara bayar yang kosong, dan catat pembayaran ke supplier di <b>Hutang Supplier</b> (kini otomatis mengurangi saldo akun).</div>
    </div>
    <div class="modal-foot"><button class="btn" id="bt_close">Tutup</button></div>`);
  bg.querySelector('#bt_close')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#bt_verif')?.addEventListener('click', () => { bg.remove(); setPayFilter('lunas_unverif'); setStatusHubTab('bayar'); goto('bayar'); });
}

/** Saldo awal PER-ENTITAS: pilih Pusat/cabang → isi kas & modal awal entitas itu. Simpan merge ke map. */
/** Saldo Awal per-entitas: kas awal dipecah PER-AKUN KAS (Tunai/Bank/QRIS, tertaut Akun Kas) + modal
 *  pemilik. Kas awal tiap akun ditulis ke cash_accounts.saldo_awal (satu sumber dg Pengaturan→Akun Kas).
 *  awalMap.kasAwal = jumlah akun (agar hitungan Neraca tetap konsisten); modalAwal per entitas. */
function dialogSaldoAwal(map: kRepo.SaldoAwalMap, entityAwal: string, cabList: string[], done: () => void): void {
  const entitas = ['PUSAT', ...cabList];
  let entity = entitas.includes(entityAwal) ? entityAwal : 'PUSAT';
  let akun: Awaited<ReturnType<typeof listAkun>> = [];
  const saldoEdit: Record<number, number> = {};                 // per akun kas → saldo awal
  const modalEdit: Record<string, number> = {};                 // per entitas → modal awal
  const tglEdit: Record<string, string> = {};                   // per entitas → tgl mulai
  for (const [k, v] of Object.entries(map)) { modalEdit[k] = v.modalAwal; tglEdit[k] = v.tglAwal; }
  const bg = openModal(`<div class="modal-head"><h3>⚙️ Saldo Awal per Entitas</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Diisi <b>sekali</b> saat mulai pembukuan. Kas awal dipecah <b>per akun</b> (Tunai/Bank/QRIS — dari Pengaturan→Akun Kas, bisa tambah bank di sana). <b>Modal pemilik</b> per entitas. Pusat = company-wide.</div>
      <div class="field"><label>Entitas</label><select id="sa_ent">${entitas.map((e) => `<option value="${esc(e)}" ${e === entity ? 'selected' : ''}>${e === 'PUSAT' ? '🏢 Pusat' : e}</option>`).join('')}</select></div>
      <div id="sa_body"><div class="empty">Memuat akun…</div></div>
      <div class="err" id="sa_err"></div></div>
    <div class="modal-foot"><button class="btn" id="sa_batal">Batal</button><button class="btn primary" id="sa_ok">💾 Simpan</button></div>`);
  const bodyEl = bg.querySelector('#sa_body') as HTMLElement;
  const akunOf = (ent: string): typeof akun => akun.filter((a) => (ent === 'PUSAT' ? !a.outlet : a.outlet === ent));
  const paint = (): void => {
    const list = akunOf(entity);
    const rows = list.length
      ? list.map((a) => `<div class="fin-row" style="padding:3px 0"><span>${a.kodeAkun ? `<span style="color:var(--muted)">${esc(a.kodeAkun)}</span> ` : ''}${esc(a.nama)} <span style="color:var(--muted);font-size:11px">(${esc(a.jenis)})</span></span>
        <input data-akun="${a.id}" inputmode="numeric" value="${rpFmt(String(saldoEdit[a.id] ?? a.saldoAwal))}" style="max-width:150px;text-align:right"></div>`).join('')
      : '<div class="scope-note" style="color:var(--muted)">Belum ada akun kas untuk entitas ini — tambah di <b>Pengaturan → Akun Kas</b> (beri kode COA mis. 1-1-2).</div>';
    bodyEl.innerHTML = `<div class="field"><label>Tanggal mulai pembukuan</label><input id="sa_tgl" type="date" value="${esc(tglEdit[entity] ?? '')}"></div>
      <div style="font-weight:600;font-size:12.5px;margin:6px 0 2px">Kas awal per akun</div>${rows}
      <div class="field" style="max-width:280px;margin-top:8px"><label>Modal ${entity === 'PUSAT' ? 'pemilik' : 'awal cabang'} (Rp)</label><input id="sa_modal" inputmode="numeric" value="${rpFmt(String(modalEdit[entity] ?? 0))}"></div>`;
    bodyEl.querySelectorAll<HTMLInputElement>('input[data-akun]').forEach((el) => el.addEventListener('input', () => { el.value = rpFmt(el.value); saldoEdit[+el.dataset['akun']!] = rpVal(el.value); }));
    const mEl = bodyEl.querySelector('#sa_modal') as HTMLInputElement; mEl.addEventListener('input', () => { mEl.value = rpFmt(mEl.value); modalEdit[entity] = rpVal(mEl.value); });
  };
  const stash = (): void => {
    bodyEl.querySelectorAll<HTMLInputElement>('input[data-akun]').forEach((el) => { saldoEdit[+el.dataset['akun']!] = rpVal(el.value); });
    const m = bodyEl.querySelector('#sa_modal') as HTMLInputElement | null; if (m) modalEdit[entity] = rpVal(m.value);
    const t = bodyEl.querySelector('#sa_tgl') as HTMLInputElement | null; if (t) tglEdit[entity] = t.value;
  };
  void (async () => { try { akun = await listAkun(); } catch { /* akun kas belum ada */ } paint(); })();
  (bg.querySelector('#sa_ent') as HTMLSelectElement).addEventListener('change', (e) => { stash(); entity = (e.target as HTMLSelectElement).value; paint(); });
  bg.querySelector('#sa_batal')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#sa_ok')?.addEventListener('click', async () => {
    stash();
    const err = bg.querySelector('#sa_err') as HTMLElement; err.textContent = '';
    try {
      // 1) Tulis saldo awal tiap akun kas yang diubah → cash_accounts (satu sumber dg Akun Kas).
      for (const a of akun) if (a.id in saldoEdit && saldoEdit[a.id] !== a.saldoAwal) await setSaldoAwal(a.id, saldoEdit[a.id]!, tglEdit[a.outlet ?? 'PUSAT'] || null);
      // 2) awalMap: kasAwal = Σ akun entitas (agar hitungan Neraca konsisten), + modal & tgl per entitas.
      const bersih: kRepo.SaldoAwalMap = {};
      for (const ent of entitas) {
        const kasAwal = akunOf(ent).reduce((s, a) => s + (saldoEdit[a.id] ?? a.saldoAwal), 0);
        const modalAwal = modalEdit[ent] ?? 0, tglAwal = tglEdit[ent] ?? '';
        if (kasAwal || modalAwal || tglAwal) bersih[ent] = { kasAwal, modalAwal, tglAwal };
      }
      await saveSetting('saldo_awal', bersih);
      bg.remove(); toast('✅ Saldo awal disimpan'); done();
    } catch (e) { err.textContent = (e as Error).message; }
  });
}

/** Kelola COA: tambah/edit/hapus akun buatan sendiri + saldo manual per-entitas. Grup otomatis dari kode. */
function dialogCoa(customInit: { kode: string; nama: string }[], saldoInit: Record<string, Record<string, number>>, entityInit: string, cabList: string[], done: () => void): void {
  const entitas = ['PUSAT', ...cabList];
  let entity = entitas.includes(entityInit) ? entityInit : 'PUSAT';
  const cust = customInit.map((c) => ({ ...c }));
  const saldo: Record<string, Record<string, number>> = JSON.parse(JSON.stringify(saldoInit || {}));
  const GRUP: Record<string, string> = { aset: 'ASET (1)', liabilitas: 'LIABILITAS (2)', ekuitas: 'EKUITAS (3)', pendapatan: 'PENDAPATAN (4)', hpp: 'HPP (5)', beban: 'BEBAN (6)' };
  const bg = openModal(`<div class="modal-head"><h3>🧾 Kelola COA (Bagan Akun)</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Tambah akun sendiri (mis. <b>2-4 Hutang jangka pendek</b>). Grup otomatis dari <b>digit pertama kode</b> (1 Aset · 2 Liabilitas · 3 Ekuitas · 4 Pendapatan · 5 HPP · 6 Beban). Akun bawaan nilainya otomatis dari data; akun buatan diisi <b>saldo manual</b> per entitas.</div>
      <div class="field" style="max-width:220px"><label>Saldo manual untuk entitas</label><select id="coa_ent">${entitas.map((e) => `<option value="${esc(e)}" ${e === entity ? 'selected' : ''}>${e === 'PUSAT' ? '🏢 Pusat' : e}</option>`).join('')}</select></div>
      <div class="row2" style="align-items:end"><div class="field"><label>Kode akun baru (berjenjang: 1-1-2)</label><input id="coa_kode" placeholder="mis. 2-4 atau 1-1-2"></div>
        <div class="field"><label>Nama akun</label><input id="coa_nama" placeholder="mis. Bank BCA / Hutang jangka pendek"></div>
        <button class="btn primary" id="coa_add" style="height:38px">➕ Tambah</button></div>
      <div id="coa_body"></div>
      <div class="err" id="coa_err"></div></div>
    <div class="modal-foot"><button class="btn" id="coa_batal">Batal</button><button class="btn primary" id="coa_ok">💾 Simpan</button></div>`);
  const body = bg.querySelector('#coa_body') as HTMLElement;
  const stash = (): void => { body.querySelectorAll<HTMLInputElement>('input[data-sal]').forEach((el) => { const k = el.dataset['sal']!; (saldo[entity] ??= {})[k] = rpVal(el.value); }); };
  const paint = (): void => {
    const merged = mergeCoa(cust);
    const grupHtml = (Object.keys(GRUP) as string[]).map((t) => {
      const akun = merged.filter((c) => tipeFromKode(c.kode) === t);
      if (!akun.length) return '';
      const neraca = ['aset', 'liabilitas', 'ekuitas'].includes(t);
      return `<div class="fin-judul" style="font-size:13px;margin-top:8px">${GRUP[t]}</div>
        <table class="data"><tbody>${akun.map((c) => {
          const bawaan = isCoaBawaan(c.kode);
          const sal = saldo[entity]?.[c.kode] ?? 0;
          const dp = Math.max(0, c.kode.split('-').length - 2) * 16; // indentasi berjenjang (1-1-2 → 1 level)
          return `<tr><td style="width:64px;color:var(--muted)">${esc(c.kode)}</td><td style="padding-left:${dp}px">${esc(c.nama)}${bawaan ? ' <span class="pill" style="background:var(--ice);color:var(--navy);font-size:10px">otomatis</span>' : ''}</td>
            <td style="width:130px">${bawaan || !neraca ? '<span style="color:var(--muted);font-size:11px">—</span>' : `<input data-sal="${esc(c.kode)}" inputmode="numeric" value="${sal ? rpFmt(String(sal)) : ''}" placeholder="Rp 0" style="max-width:120px;text-align:right">`}</td>
            <td style="width:34px">${bawaan ? '' : `<button class="ico-btn" data-del="${esc(c.kode)}" title="Hapus akun">🗑️</button>`}</td></tr>`;
        }).join('')}</tbody></table>`;
    }).join('');
    body.innerHTML = grupHtml;
    body.querySelectorAll<HTMLInputElement>('input[data-sal]').forEach((el) => el.addEventListener('input', () => { el.value = el.value.trim() ? rpFmt(el.value) : ''; }));
    body.querySelectorAll<HTMLElement>('button[data-del]').forEach((b) => b.addEventListener('click', () => {
      stash(); const kode = b.dataset['del']!; const i = cust.findIndex((c) => c.kode === kode); if (i >= 0) cust.splice(i, 1);
      for (const e of Object.keys(saldo)) delete saldo[e]?.[kode]; paint();
    }));
  };
  paint();
  (bg.querySelector('#coa_ent') as HTMLSelectElement).addEventListener('change', (e) => { stash(); entity = (e.target as HTMLSelectElement).value; paint(); });
  bg.querySelector('#coa_add')?.addEventListener('click', () => {
    const err = bg.querySelector('#coa_err') as HTMLElement; err.textContent = '';
    const kode = (bg.querySelector('#coa_kode') as HTMLInputElement).value.trim();
    const nama = (bg.querySelector('#coa_nama') as HTMLInputElement).value.trim();
    if (!/^[1-6](-\d{1,3}){1,3}$/.test(kode)) { err.textContent = 'Kode: N-N atau berjenjang N-N-N (mis. 2-4 atau 1-1-2), diawali 1–6.'; return; }
    if (!nama) { err.textContent = 'Nama akun wajib diisi.'; return; }
    if (isCoaBawaan(kode)) { err.textContent = `Kode ${kode} sudah dipakai akun bawaan.`; return; }
    const ada = cust.find((c) => c.kode === kode);
    if (ada) { err.textContent = `Kode ${kode} sudah ada — dipakai akun "${ada.nama}". Pakai kode lain atau hapus dulu.`; return; }
    stash();
    cust.push({ kode, nama });
    (bg.querySelector('#coa_kode') as HTMLInputElement).value = ''; (bg.querySelector('#coa_nama') as HTMLInputElement).value = '';
    paint();
  });
  bg.querySelector('#coa_batal')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#coa_ok')?.addEventListener('click', async () => {
    stash();
    const bersihSaldo: Record<string, Record<string, number>> = {};
    for (const [e, m] of Object.entries(saldo)) { const mm: Record<string, number> = {}; for (const [k, v] of Object.entries(m)) if (v) mm[k] = v; if (Object.keys(mm).length) bersihSaldo[e] = mm; }
    try { await saveSetting('coa_custom', cust); await saveSetting('coa_saldo', bersihSaldo); bg.remove(); toast('✅ COA disimpan'); done(); }
    catch (e) { (bg.querySelector('#coa_err') as HTMLElement).textContent = (e as Error).message; }
  });
}

async function dialogAset(done: () => void): Promise<void> {
  let rows: kRepo.AsetRow[];
  try { rows = await kRepo.listAset(); } catch (e) { toast((e as Error).message); return; }
  const hari = todayISO();
  const isi = rows.map((a) => `<tr><td><b>${esc(a.nama)}</b>${a.kategori ? `<br><span style="font-size:11px;color:var(--muted)">${esc(a.kategori)}</span>` : ''}</td>
      <td>${esc(a.tglBeli)}</td><td class="r">${rp(a.harga)}</td>
      <td class="r">${a.umurBulan ? `${a.umurBulan} bln` : 'tak disusutkan'}${a.metodeSusut === 'saldo_menurun' ? '<br><span style="font-size:10.5px;color:var(--muted)">saldo menurun</span>' : ''}</td>
      <td class="r">${rp(nilaiBuku(a, hari))}</td>
      <td><button class="btn" style="padding:3px 8px;border-color:var(--danger);color:var(--danger)" data-adel="${a.id}">🗑️</button></td></tr>`).join('')
    || '<tr><td colspan="6" class="empty">Belum ada aset. Tambahkan etalase, alat servis, kendaraan, dsb.</td></tr>';

  const bg = openModal(`<div class="modal-head"><h3>🏗️ Aset Tetap</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Belanja aset <b>tidak</b> mengurangi laba — yang mengurangi laba adalah <b>penyusutannya</b> tiap bulan. Umur ekonomis lazim: peralatan 48 bln, perabot 60 bln, kendaraan 96 bln.</div>
      <div class="row2">
        <div class="field"><label>Nama aset</label><input id="as_nama" placeholder="Etalase kaca"></div>
        <div class="field"><label>Kategori</label><select id="as_kat"><option>Peralatan</option><option>Perabot</option><option>Kendaraan</option><option>Lainnya</option></select></div>
      </div>
      <div class="row2">
        <div class="field"><label>Harga perolehan (Rp)</label><input id="as_harga" inputmode="numeric" value="Rp 0"></div>
        <div class="field"><label>Tanggal beli</label><input id="as_tgl" type="date" value="${esc(hari)}"></div>
      </div>
      <div class="row2">
        <div class="field"><label>Kelompok (pajak)</label><select id="as_kel"><option value="">— pilih untuk mengisi umur otomatis —</option>${opsiKelompok()}</select>
          <div class="hint">Masa manfaat menurut UU PPh Ps. 11 / PMK 96/2009. Umur di bawah tetap boleh diubah manual.</div></div>
        <div class="field"><label>Metode penyusutan</label><select id="as_metode">
          <option value="garis_lurus">Garis lurus — beban sama tiap bulan</option>
          <option value="saldo_menurun">Saldo menurun — beban besar di awal</option></select>
          <div class="hint" id="as_metode_hint">Bangunan wajib garis lurus (Ps. 11 ayat 6).</div></div>
      </div>
      <div class="row2">
        <div class="field"><label>Umur ekonomis (bulan)</label><input id="as_umur" type="number" min="0" value="48"><div class="hint">0 = tak disusutkan (mis. tanah)</div></div>
        <div class="field"><label>Nilai sisa (Rp)</label><input id="as_sisa" inputmode="numeric" value="Rp 0"></div>
      </div>
      <div class="err" id="as_err"></div>
      <button class="btn primary" id="as_add" style="margin-bottom:12px">➕ Tambah Aset</button>
      <div style="overflow-x:auto"><table class="data"><thead><tr><th>Aset</th><th>Beli</th><th class="r">Harga</th><th class="r">Umur</th><th class="r">Nilai buku</th><th></th></tr></thead><tbody>${isi}</tbody></table></div>
    </div>
    <div class="modal-foot"><button class="btn primary" id="as_tutup">Tutup</button></div>`);

  ['as_harga', 'as_sisa'].forEach((id) => {
    const el = bg.querySelector('#' + id) as HTMLInputElement;
    el.addEventListener('input', () => { el.value = rpFmt(el.value); });
  });
  // Kelompok pajak mengisi umur otomatis, dan bangunan mengunci metodenya jadi garis lurus —
  // memilihkan yang benar lebih baik daripada menegur setelah salah simpan.
  const selKel = bg.querySelector('#as_kel') as HTMLSelectElement;
  const selMet = bg.querySelector('#as_metode') as HTMLSelectElement;
  selKel.addEventListener('change', () => {
    const k = selKel.value as keyof typeof KELOMPOK_ASET | '';
    if (k && KELOMPOK_ASET[k]) (bg.querySelector('#as_umur') as HTMLInputElement).value = String(KELOMPOK_ASET[k].tahun * 12);
    const kunci = wajibGarisLurus(k);
    if (kunci) selMet.value = 'garis_lurus';
    selMet.disabled = kunci;
    (bg.querySelector('#as_metode_hint') as HTMLElement).textContent = kunci
      ? 'Bangunan wajib garis lurus (Ps. 11 ayat 6) — pilihan dikunci.'
      : 'Saldo menurun: beban lebih besar di tahun-tahun awal, sisa nilai buku dihabiskan di akhir masa manfaat.';
  });
  bg.querySelector('#as_tutup')?.addEventListener('click', () => { bg.remove(); done(); });
  bg.querySelector('#as_add')?.addEventListener('click', async () => {
    const v = (id: string): string => (bg.querySelector('#' + id) as HTMLInputElement | HTMLSelectElement).value;
    const err = bg.querySelector('#as_err') as HTMLElement;
    const harga = rpVal(v('as_harga'));
    if (!v('as_nama').trim()) { err.textContent = 'Nama aset wajib diisi.'; return; }
    if (harga <= 0) { err.textContent = 'Harga perolehan harus lebih dari 0.'; return; }
    try {
      await kRepo.simpanAset(null, {
        nama: v('as_nama').trim(), kategori: v('as_kat'), harga, tglBeli: v('as_tgl'),
        umurBulan: Math.max(0, +v('as_umur') || 0), nilaiSisa: rpVal(v('as_sisa')),
        kelompok: v('as_kel') || null,
        metodeSusut: (wajibGarisLurus(v('as_kel')) ? 'garis_lurus' : v('as_metode')) as 'garis_lurus' | 'saldo_menurun',
        outlet: currentUser()?.outlet ?? null, catatan: null, dijualTgl: null, dijualHarga: null,
        byUser: currentUser()?.nama ?? '',
      });
      bg.remove(); toast('✅ Aset ditambahkan'); done(); void dialogAset(done);
    } catch (e) { err.textContent = (e as Error).message; }
  });
  bg.querySelectorAll<HTMLElement>('button[data-adel]').forEach((b) => b.addEventListener('click', async () => {
    if (!confirm('Hapus aset ini? Nilai bukunya akan hilang dari Posisi Keuangan.')) return;
    try { await kRepo.hapusAset(+(b.dataset['adel'] ?? 0)); bg.remove(); done(); void dialogAset(done); }
    catch (e) { toast((e as Error).message); }
  }));
}

async function dialogEkuitas(cabList: string[], done: () => void): Promise<void> {
  let rows: kRepo.EkuitasRow[];
  try { rows = await kRepo.listEkuitas(); } catch (e) { toast((e as Error).message); return; }
  const entLbl = (o: string | null): string => o ?? 'Pusat';
  const isi = rows.map((r) => `<tr><td>${esc(r.tgl)}</td>
      <td>${r.jenis === 'setoran' ? '<span class="pill" style="background:var(--okbg);color:var(--ok)">Setoran</span>' : '<span class="pill" style="background:var(--dangerbg);color:var(--danger)">Prive</span>'}</td>
      <td>${esc(entLbl(r.outlet))}</td><td class="r">${rp(r.jumlah)}</td><td>${esc(r.catatan ?? '—')}</td>
      <td><button class="btn" style="padding:3px 8px;border-color:var(--danger);color:var(--danger)" data-edel="${r.id}">🗑️</button></td></tr>`).join('')
    || '<tr><td colspan="6" class="empty">Belum ada pergerakan modal.</td></tr>';

  const bg = openModal(`<div class="modal-head"><h3>💼 Modal & Pengambilan Pemilik</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note"><b>Setoran</b> = uang pemilik masuk ke usaha. <b>Prive</b> = uang usaha diambil pemilik. Keduanya <b>bukan</b> pendapatan/beban. Pilih <b>entitas</b> (Pusat = modal induk, atau cabang tertentu).</div>
      <div class="row2">
        <div class="field"><label>Jenis</label><select id="eq_jenis"><option value="setoran">Setoran modal</option><option value="prive">Prive (pengambilan)</option></select></div>
        <div class="field"><label>Entitas</label><select id="eq_out"><option value="">🏢 Pusat</option>${cabList.map((c) => `<option value="${esc(c)}">${esc(c)}</option>`).join('')}</select></div>
      </div>
      <div class="row2">
        <div class="field"><label>Tanggal</label><input id="eq_tgl" type="date" value="${esc(todayISO())}"></div>
        <div class="field"><label>Jumlah (Rp)</label><input id="eq_jml" inputmode="numeric" value="Rp 0"></div>
      </div>
      <div class="field"><label>Catatan</label><input id="eq_cat" placeholder="mis. tambah modal etalase"></div>
      <div class="err" id="eq_err"></div>
      <button class="btn primary" id="eq_add" style="margin:8px 0 12px">➕ Catat</button>
      <div style="overflow-x:auto"><table class="data"><thead><tr><th>Tanggal</th><th>Jenis</th><th>Entitas</th><th class="r">Jumlah</th><th>Catatan</th><th></th></tr></thead><tbody>${isi}</tbody></table></div>
    </div>
    <div class="modal-foot"><button class="btn primary" id="eq_tutup">Tutup</button></div>`);

  const jml = bg.querySelector('#eq_jml') as HTMLInputElement;
  jml.addEventListener('input', () => { jml.value = rpFmt(jml.value); });
  bg.querySelector('#eq_tutup')?.addEventListener('click', () => { bg.remove(); done(); });
  bg.querySelector('#eq_add')?.addEventListener('click', async () => {
    const err = bg.querySelector('#eq_err') as HTMLElement;
    const n = rpVal(jml.value);
    if (n <= 0) { err.textContent = 'Jumlah harus lebih dari 0.'; return; }
    try {
      await kRepo.tambahEkuitas({
        tgl: (bg.querySelector('#eq_tgl') as HTMLInputElement).value,
        jenis: (bg.querySelector('#eq_jenis') as HTMLSelectElement).value as 'setoran' | 'prive',
        jumlah: n, outlet: (bg.querySelector('#eq_out') as HTMLSelectElement).value || null,
        catatan: (bg.querySelector('#eq_cat') as HTMLInputElement).value.trim(),
        by_user: currentUser()?.nama ?? '',
      });
      bg.remove(); toast('✅ Tercatat'); done(); void dialogEkuitas(cabList, done);
    } catch (e) { err.textContent = (e as Error).message; }
  });
  bg.querySelectorAll<HTMLElement>('button[data-edel]').forEach((b) => b.addEventListener('click', async () => {
    if (!confirm('Hapus catatan ini?')) return;
    try { await kRepo.hapusEkuitas(+(b.dataset['edel'] ?? 0)); bg.remove(); done(); void dialogEkuitas(cabList, done); }
    catch (e) { toast((e as Error).message); }
  }));
}
